import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ClipboardCheck, Inbox, LayoutList, Megaphone, Plus, Radio, Send, ShieldAlert, Star } from 'lucide-react';
import { AdAccount } from '../../api/ads/ad-account.api';
import {
  BrandCampaign,
  BrandCampaignVisibility,
  closeBrandCampaign,
  getBusinessPendingRatings,
  getMyBrandCampaigns,
  submitBrandCampaign,
} from '../../api/payment/brand-deal.api';
import { campaignStatusLabel, dealStatusLabel } from '../../utils/brand-deal';
import { countdownLabel } from '../../utils/brand-creative';
import {
  BusinessRequests,
  EMPTY_BUSINESS_REQUESTS,
  ReceivedApplicationItem,
  ReceivedSubmissionItem,
  SentInviteItem,
  loadBusinessRequests,
} from '../../utils/brand-deal-requests';
import { GroupedNavGroup, GroupedSectionNavPills } from '../section-nav/grouped-section-nav';
import BrandBookingList from '../brand-live/brand-booking-list';
import BrandCampaignForm from './brand-campaign-form';
import BrandCampaignDetailView from './brand-campaign-detail';
import BrandTaskReview from './brand-task-review';
import BrandRatingPendingList, { BrandRatingPendingListHandle } from './brand-rating-pending';
import { showOrderErrorToast } from './order-error-toast';
import { OrdererSanctionProvider, useOrdererSanction } from '../brand-sanction/orderer-sanction-context';
import OrdererSanctionBanner from '../brand-sanction/orderer-sanction-banner';
import OrdererSanctionHistory from '../brand-sanction/orderer-sanction-history';
import { SANCTION_LOCK_HINT } from '../../utils/brand-sanction';
import { ordererProfilePath } from '../../utils/brand-profile';
import CampaignTypePicker from './campaign-type-picker';

// Mục "Hợp tác creator" trong trang Quảng cáo (Task 7) — chỉ mount khi doanh nghiệp có tài khoản
// Verified (xem ads.page.tsx). Chia theo chiều yêu cầu (sếp góp ý 07/10), giống trang creator:
// chiến dịch của mình / yêu cầu mình gửi creator / yêu cầu creator gửi tới mình / đánh giá.
// Màn tạo-sửa, chi tiết chiến dịch và bàn duyệt bài vẫn mở chồng lên như trước.

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const formatVnd = (v: number) => `${Number(v || 0).toLocaleString('vi-VN')}đ`;
const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('vi-VN') : '');

const STATUS_CLS: Record<string, string> = {
  Draft: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  PendingReview: 'bg-amber-100 text-amber-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  Rejected: 'bg-rose-100 text-rose-700',
  Closed: 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
};

const OUTLINE_BTN =
  'rounded-md border ui-divider px-3 py-1.5 text-sm ui-text hover:bg-black/5 dark:hover:bg-white/5';
const PRIMARY_BTN = 'rounded-md bg-[#0071A6] px-3 py-1.5 text-sm text-white hover:brightness-110 disabled:opacity-50';

export type BusinessDealTab =
  | 'campaigns'
  | 'sent-invites'
  | 'sent-bookings'
  | 'received-applications'
  | 'received-submissions'
  | 'ratings'
  | 'sanctions';

type View =
  | { mode: 'list' }
  | { mode: 'create'; visibility?: BrandCampaignVisibility }
  | { mode: 'create-brief'; visibility?: BrandCampaignVisibility }
  | { mode: 'create-proposal'; visibility?: BrandCampaignVisibility }
  | { mode: 'edit'; campaign: BrandCampaign }
  | { mode: 'detail'; campaignId: string; initialTab?: 'invite' | 'applications' }
  | { mode: 'task-review'; dealId: string };

interface BrandCampaignSectionProps {
  /** Tài khoản doanh nghiệp đã Verified của người dùng hiện tại — section chỉ mount khi mảng này không rỗng. */
  bizAccounts: AdAccount[];
}

// ─── Các thành phần con, khai ngoài component cha để không bị dựng lại cả cây mỗi lần render ───

const CampaignThumb: React.FC<{ c: BrandCampaign; size?: string }> = ({ c, size = 'size-14' }) =>
  c.images[0]?.url ? (
    <img src={c.images[0].url} alt="" className={`${size} shrink-0 rounded-md object-cover`} />
  ) : c.videos[0]?.url ? (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video src={c.videos[0].url} className={`${size} shrink-0 rounded-md bg-black object-cover`} muted />
  ) : (
    <div className={`${size} shrink-0 rounded-md bg-slate-100 dark:bg-slate-700`} />
  );

const CampaignRow: React.FC<{
  c: BrandCampaign;
  busy: boolean;
  /** Đang bị khoá tạo việc: sửa và nộp duyệt bị chặn ở BE nên mờ nút (RB8). */
  locked: boolean;
  onDetail: () => void;
  onEdit: () => void;
  onSubmit: () => void;
  onClose: () => void;
}> = ({ c, busy, locked, onDetail, onEdit, onSubmit, onClose }) => (
  <div className="flex items-center gap-3 rounded-lg border ui-divider p-3">
    <CampaignThumb c={c} />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="truncate font-medium ui-text">{c.title}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_CLS[c.status] || 'bg-slate-200 text-slate-700'}`}>
          {campaignStatusLabel(c.status)}
        </span>
        {c.kind === 'Creative' && (
          <span className="rounded-full bg-[#EAEFFA] px-2 py-0.5 text-[11px] font-semibold text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
            Brief sáng tạo
          </span>
        )}
        {c.kind === 'Proposal' && (
          <span className="rounded-full bg-[#EAEFFA] px-2 py-0.5 text-[11px] font-semibold text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
            Nhận đề xuất
          </span>
        )}
      </div>
      <div className="truncate text-xs ui-muted">{c.destinationUrl}</div>
      {c.status === 'Rejected' && c.rejectReason ? (
        <div className="text-xs text-rose-600">Lý do từ chối: {c.rejectReason}</div>
      ) : null}
    </div>
    <div className="flex shrink-0 flex-col gap-1.5">
      <button onClick={onDetail} className={OUTLINE_BTN}>
        Chi tiết
      </button>
      {c.status !== 'Closed' && (
        <button
          disabled={locked}
          title={locked ? SANCTION_LOCK_HINT : undefined}
          onClick={onEdit}
          className={`${OUTLINE_BTN} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          Sửa
        </button>
      )}
      {c.status === 'Draft' && (
        <button
          disabled={busy || locked}
          title={locked ? SANCTION_LOCK_HINT : undefined}
          onClick={onSubmit}
          className={PRIMARY_BTN}
        >
          Nộp duyệt
        </button>
      )}
      {c.status === 'Approved' && (
        <button
          disabled={busy}
          onClick={onClose}
          className="rounded-md border border-rose-200 px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-900/40 dark:hover:bg-rose-900/20"
        >
          Đóng chiến dịch
        </button>
      )}
    </div>
  </div>
);

const CreatorAvatar: React.FC<{ name: string; avatar: string | null | undefined }> = ({ name, avatar }) => (
  <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#EAEFFA] text-sm font-bold text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
    {avatar ? <img src={avatar} alt="" className="size-full object-cover" /> : name.trim()[0]?.toUpperCase() || '?'}
  </div>
);

const SentInviteRow: React.FC<{ item: SentInviteItem; onOpen: () => void }> = ({ item, onOpen }) => {
  const { campaign, deal } = item;
  const waiting = deal.status === 'Invited';
  return (
    <div className="flex items-center gap-3 rounded-lg border ui-divider p-3" data-testid="sent-invite">
      <CampaignThumb c={campaign} size="size-10" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium ui-text">{campaign.title}</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs ui-muted">
          <span>Giá {formatVnd(deal.priceVnd)}</span>
          {deal.endsAt && <span>Kết thúc {formatDate(deal.endsAt)}</span>}
        </div>
      </div>
      <span
        className={`shrink-0 rounded-full px-2.5 py-1 text-[12px] font-semibold ${
          waiting
            ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
            : 'border ui-divider ui-text'
        }`}
      >
        {waiting ? 'Chờ creator trả lời' : dealStatusLabel(deal.status)}
      </span>
      <button onClick={onOpen} className={`shrink-0 ${OUTLINE_BTN}`}>
        Xem
      </button>
    </div>
  );
};

const ReceivedApplicationRow: React.FC<{ item: ReceivedApplicationItem; onOpen: () => void }> = ({ item, onOpen }) => {
  const { campaign, application } = item;
  const { creator } = application;
  return (
    <div className="flex items-start gap-3 rounded-lg border ui-divider p-3" data-testid="received-application">
      <CreatorAvatar name={creator.displayName} avatar={creator.avatar} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium ui-text">{creator.displayName}</div>
        <div className="truncate text-xs ui-muted">
          Ứng tuyển vào <span className="font-semibold ui-text">{campaign.title}</span> ·{' '}
          {formatDate(application.createdAt)}
        </div>
        <div className="truncate text-xs ui-muted">
          {creator.followers.toLocaleString('vi-VN')} người theo dõi · {creator.completedDeals} deal đã hoàn thành
        </div>
        {application.message && <p className="mt-1 line-clamp-2 text-sm ui-text">{application.message}</p>}
      </div>
      <button onClick={onOpen} className={`shrink-0 ${PRIMARY_BTN}`}>
        Xem đơn
      </button>
    </div>
  );
};

const ReceivedSubmissionRow: React.FC<{ item: ReceivedSubmissionItem; nowMs: number; onOpen: () => void }> = ({
  item,
  nowMs,
  onOpen,
}) => {
  const { campaign, task } = item;
  const name = task.creator?.displayName || 'Creator';
  const due = countdownLabel(task.reviewDueAt, nowMs);
  return (
    <div className="flex items-start gap-3 rounded-lg border ui-divider p-3" data-testid="received-submission">
      <CreatorAvatar name={name} avatar={task.creator?.avatar} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium ui-text">{name}</div>
        <div className="truncate text-xs ui-muted">
          Nộp bài cho <span className="font-semibold ui-text">{campaign.title}</span>
          {task.latestVersion > 1 ? ` · bản thứ ${task.latestVersion}` : ''}
        </div>
        <div className="flex flex-wrap gap-x-3 text-xs">
          {due && <span className="font-semibold text-amber-600 dark:text-amber-400">{due} để duyệt</span>}
          {task.cancelRequest && <span className="ui-muted">Có yêu cầu huỷ chờ duyệt</span>}
        </div>
      </div>
      <button onClick={onOpen} className={`shrink-0 ${PRIMARY_BTN}`}>
        Duyệt bài
      </button>
    </div>
  );
};

const LoadErrorNote: React.FC<{ failed: number; onRetry: () => void }> = ({ failed, onRetry }) =>
  failed > 0 ? (
    <p className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
      Không tải được {failed} chiến dịch nên danh sách có thể chưa đủ.
      <button onClick={onRetry} className="font-semibold underline">
        Tải lại
      </button>
    </p>
  ) : null;

/** Lối vào trang hồ sơ đánh giá của từng tài khoản đặt đơn (spec 2026-10-12 §4), kể cả tài khoản cá nhân. */
const OrdererProfileLinks: React.FC<{ accounts: AdAccount[] }> = ({ accounts }) => (
  <div className="flex flex-wrap gap-3 text-[13px]">
    {accounts.map((a) => (
      <a key={a.id} href={ordererProfilePath(a.id)} className="text-[#0057FF] underline-offset-2 hover:underline dark:text-blue-300">
        Hồ sơ đánh giá của {a.businessName}
      </a>
    ))}
  </div>
);

const BrandCampaignSectionInner: React.FC<BrandCampaignSectionProps> = ({ bizAccounts }) => {
  // Xử phạt người đặt đơn (spec 2026-10-09-brand-orderer-sanctions §5): đang bị khoá tạo việc thì nút tạo mới
  // mờ kèm chú thích, việc đang chạy vẫn xem/duyệt bình thường. Lịch sử xử phạt có mục riêng khi đã từng bị phạt.
  const { blocked, history: sanctionHistory } = useOrdererSanction();
  const [campaigns, setCampaigns] = useState<BrandCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>({ mode: 'list' });
  // Một nút "Tạo chiến dịch" mở hộp chọn loại. Nút "Tạo chiến dịch thuê creator" ở Sàn hợp tác dẫn sang đây kèm ?create=1.
  const [pickerOpen, setPickerOpen] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('create') === '1';
    } catch {
      return false;
    }
  });
  const [tab, setTab] = useState<BusinessDealTab>('campaigns');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [requests, setRequests] = useState<BusinessRequests>(EMPTY_BUSINESS_REQUESTS);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // M5 (Task 3): dải nhắc "Bạn có N deal cần đánh giá" — tải riêng ngay từ đầu (không chờ mở mục
  // "Đánh giá"), `BrandRatingPendingList` tự đồng bộ lại qua `onCountChange`.
  const [pendingRatingsCount, setPendingRatingsCount] = useState(0);
  const ratingsRef = useRef<BrandRatingPendingListHandle>(null);
  const goToRatingsTab = () => {
    setView({ mode: 'list' });
    setTab('ratings');
  };
  // Đánh giá từ chi tiết chiến dịch hoặc bàn duyệt bài phải giảm đếm ngay, không chờ mở lại mục "Đánh giá".
  const handleRated = () => setPendingRatingsCount((n) => Math.max(0, n - 1));

  useEffect(() => {
    getBusinessPendingRatings()
      .then((res) => setPendingRatingsCount(res.length))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const loadRequests = (list: BrandCampaign[]) => {
    setRequestsLoading(true);
    loadBusinessRequests(list)
      .then(setRequests)
      .catch(() => setRequests(EMPTY_BUSINESS_REQUESTS))
      .finally(() => setRequestsLoading(false));
  };

  const load = () => {
    setLoading(true);
    getMyBrandCampaigns()
      // Chiến dịch loại đặt quảng cáo nằm ở mục "Đơn đặt quảng cáo" riêng.
      .then((all) => {
        const list = all.filter((c) => c.kind !== 'Booking');
        setCampaigns(list);
        loadRequests(list);
      })
      .catch(() => {
        toast.error('Không tải được danh sách chiến dịch hợp tác creator.');
        setRequestsLoading(false);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backToList = () => {
    setView({ mode: 'list' });
    load();
  };

  const submitForReview = async (c: BrandCampaign) => {
    try {
      setBusyId(c.id);
      await submitBrandCampaign(c.id);
      toast.success('Đã nộp duyệt, chờ admin xét duyệt.');
      load();
    } catch (e) {
      showOrderErrorToast(e, 'Nộp duyệt thất bại.', goToRatingsTab);
    } finally {
      setBusyId(null);
    }
  };

  const close = async (c: BrandCampaign) => {
    // M-6 (final review): đóng chiến dịch qua M1 khi sàn đang Open thì BE cũng tự đóng luôn sàn (hoàn phần
    // ký quỹ sàn chưa dùng về ví + đóng mọi đơn đang chờ) — confirm phải nói rõ việc này, không chỉ nói về
    // creator/deal đang chạy.
    const listingNote =
      c.listingStatus === 'Open'
        ? ' Sàn chiến dịch cũng sẽ đóng, phần ký quỹ sàn chưa dùng được hoàn về ví và các đơn đang chờ sẽ đóng.'
        : '';
    if (
      !window.confirm(
        `Đóng chiến dịch "${c.title}"? Sẽ không mời thêm creator mới (deal đang chạy vẫn tiếp tục tới khi kết thúc).${listingNote}`
      )
    )
      return;
    try {
      setBusyId(c.id);
      await closeBrandCampaign(c.id);
      toast.success('Đã đóng chiến dịch.');
      load();
    } catch (e) {
      toast.error(errorMessage(e, 'Đóng chiến dịch thất bại.'));
    } finally {
      setBusyId(null);
    }
  };

  if (view.mode === 'create' || view.mode === 'create-brief' || view.mode === 'create-proposal' || view.mode === 'edit') {
    const isBriefCreate = view.mode === 'create-brief';
    const isProposalCreate = view.mode === 'create-proposal';
    const createVisibility = view.mode === 'edit' ? undefined : view.visibility;
    const visibilitySuffix =
      createVisibility === 'Public' ? ' (công khai)' : createVisibility === 'Private' ? ' (riêng tư)' : '';
    return (
      <div className="rounded-xl border ui-divider p-4 space-y-3">
        <h2 className="font-semibold ui-text">
          {view.mode === 'edit'
            ? 'Sửa chiến dịch hợp tác creator'
            : isProposalCreate
            ? 'Tạo đơn nhận đề xuất'
            : isBriefCreate
            ? 'Tạo brief sáng tạo'
            : 'Tạo chiến dịch hợp tác creator'}
          {visibilitySuffix}
        </h2>
        <BrandCampaignForm
          initial={view.mode === 'edit' ? view.campaign : null}
          bizAccounts={bizAccounts}
          kind={isProposalCreate ? 'Proposal' : isBriefCreate ? 'Creative' : 'Direct'}
          visibility={createVisibility}
          onSaved={() => {
            setTab('campaigns');
            backToList();
          }}
          onCancel={() => setView({ mode: 'list' })}
          onGoToRatingsTab={goToRatingsTab}
        />
      </div>
    );
  }

  if (view.mode === 'detail') {
    return (
      <div className="rounded-xl border ui-divider p-4">
        <BrandCampaignDetailView
          campaignId={view.campaignId}
          bizAccounts={bizAccounts}
          initialTab={view.initialTab}
          onBack={backToList}
          onGoToRatingsTab={goToRatingsTab}
          onRated={handleRated}
        />
      </div>
    );
  }

  if (view.mode === 'task-review') {
    return (
      <div className="rounded-xl border ui-divider p-4">
        <BrandTaskReview dealId={view.dealId} viewerRole="Business" onBack={backToList} onRated={handleRated} />
      </div>
    );
  }

  const { sentInvites, receivedApplications, receivedSubmissions, failedCampaigns } = requests;
  const waitingInvites = sentInvites.filter((i) => i.deal.status === 'Invited');
  const otherInvites = sentInvites.filter((i) => i.deal.status !== 'Invited');
  const openDetail = (campaignId: string, initialTab?: 'invite' | 'applications') =>
    setView({ mode: 'detail', campaignId, initialTab });
  const retryRequests = () => loadRequests(campaigns);

  const navGroups: GroupedNavGroup<BusinessDealTab>[] = [
    {
      title: 'Chiến dịch của tôi',
      items: [{ key: 'campaigns', label: 'Danh sách chiến dịch', Icon: LayoutList }],
    },
    {
      title: 'Yêu cầu đã gửi',
      items: [
        { key: 'sent-invites', label: 'Lời mời gửi creator', Icon: Send },
        { key: 'sent-bookings', label: 'Đơn đặt quảng cáo', Icon: Radio },
      ],
    },
    {
      title: 'Yêu cầu nhận được',
      items: [
        { key: 'received-applications', label: 'Đơn ứng tuyển', Icon: Inbox, badge: receivedApplications.length },
        { key: 'received-submissions', label: 'Bài nộp chờ duyệt', Icon: ClipboardCheck, badge: receivedSubmissions.length },
      ],
    },
    {
      title: 'Sau hợp tác',
      items: [
        { key: 'ratings', label: 'Đánh giá', Icon: Star, badge: pendingRatingsCount },
        ...(sanctionHistory.length > 0
          ? [{ key: 'sanctions' as BusinessDealTab, label: 'Lịch sử xử phạt', Icon: ShieldAlert }]
          : []),
      ],
    },
  ];

  return (
    <div className="rounded-xl border ui-divider p-4 space-y-4">
      {pickerOpen && !blocked && (
        <CampaignTypePicker
          bizAccounts={bizAccounts}
          onClose={() => setPickerOpen(false)}
          onPickForm={(mode, visibility) => {
            setPickerOpen(false);
            setView({ mode, visibility });
          }}
          onGoToRatingsTab={goToRatingsTab}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-semibold ui-text">
          <Megaphone size={18} className="text-[#0071A6]" /> Hợp tác creator
        </h2>
        <button
          disabled={blocked}
          title={blocked ? SANCTION_LOCK_HINT : undefined}
          onClick={() => setPickerOpen(true)}
          className="flex items-center gap-1 rounded-md bg-[#0071A6] px-3 py-1.5 text-sm text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus size={14} /> Tạo chiến dịch
        </button>
      </div>
      {blocked ? (
        <OrdererSanctionBanner />
      ) : (
        <p className="-mt-2 text-xs ui-muted">
          Gửi deal thẳng tới creator đã xác minh: đặt giá riêng từng người, ký quỹ từ ví doanh nghiệp hoặc Ca nạp của bạn, creator đăng
          bài và nhận tiền khi giữ đủ thời gian.
        </p>
      )}

      {/* M5: dải nhắc "Có N deal đang chờ bạn đánh giá" — hiện ở mọi mục trừ chính mục "Đánh giá". */}
      {pendingRatingsCount > 0 && tab !== 'ratings' && (
        <button
          type="button"
          onClick={goToRatingsTab}
          className="flex w-full items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-left text-sm font-semibold text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300"
        >
          <span>Có {pendingRatingsCount} deal đang chờ bạn đánh giá.</span>
          <span className="shrink-0 rounded-full border border-amber-300 px-2.5 py-1 text-[12px] dark:border-amber-700">
            Đánh giá ngay
          </span>
        </button>
      )}

      {/* Mục lục dạng nút tròn theo nhóm, cùng kiểu trang creator; nằm lọt trong trang Quảng cáo nên
          không dùng cột trái. */}
      <GroupedSectionNavPills inline groups={navGroups} active={tab} onSelect={setTab} className="border-y ui-divider py-3" />

      {tab === 'campaigns' &&
        (loading ? (
          <p className="text-sm ui-muted">Đang tải…</p>
        ) : campaigns.length === 0 ? (
          <p className="text-sm ui-muted">Chưa có chiến dịch nào.</p>
        ) : (
          <div className="space-y-2">
            {campaigns.map((c) => (
              <CampaignRow
                key={c.id}
                c={c}
                busy={busyId === c.id}
                locked={blocked}
                onDetail={() => openDetail(c.id)}
                onEdit={() => setView({ mode: 'edit', campaign: c })}
                onSubmit={() => submitForReview(c)}
                onClose={() => close(c)}
              />
            ))}
          </div>
        ))}

      {tab === 'sent-invites' && (
        <div className="space-y-3">
          <p className="text-xs ui-muted">
            Lời mời bạn gửi thẳng tới creator trong các chiến dịch đang chạy. Bấm Xem để huỷ lời mời hoặc xem bài đăng.
          </p>
          <LoadErrorNote failed={failedCampaigns} onRetry={retryRequests} />
          {requestsLoading ? (
            <p className="text-sm ui-muted">Đang tải…</p>
          ) : sentInvites.length === 0 ? (
            <p className="text-sm ui-muted">Chưa gửi lời mời nào. Mở một chiến dịch đang chạy để mời creator.</p>
          ) : (
            <>
              {waitingInvites.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold ui-text">Đang chờ trả lời ({waitingInvites.length})</h3>
                  {waitingInvites.map((it) => (
                    <SentInviteRow key={it.deal.id} item={it} onOpen={() => openDetail(it.campaign.id)} />
                  ))}
                </div>
              )}
              {otherInvites.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold ui-text">Đã có kết quả ({otherInvites.length})</h3>
                  {otherInvites.map((it) => (
                    <SentInviteRow key={it.deal.id} item={it} onOpen={() => openDetail(it.campaign.id)} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'sent-bookings' && (
        <div className="space-y-2">
          <p className="text-xs ui-muted">Đơn đặt bài đăng hoặc quảng cáo livestream bạn gửi tới creator.</p>
          <BrandBookingList bizAccounts={bizAccounts} onGoToRatingsTab={goToRatingsTab} />
        </div>
      )}

      {tab === 'received-applications' && (
        <div className="space-y-2">
          <p className="text-xs ui-muted">Creator xin tham gia chiến dịch bạn mở trên sàn, đang chờ bạn duyệt.</p>
          <LoadErrorNote failed={failedCampaigns} onRetry={retryRequests} />
          {requestsLoading ? (
            <p className="text-sm ui-muted">Đang tải…</p>
          ) : receivedApplications.length === 0 ? (
            <p className="text-sm ui-muted">Chưa có đơn ứng tuyển nào đang chờ.</p>
          ) : (
            receivedApplications.map((it) => (
              <ReceivedApplicationRow
                key={it.application.id}
                item={it}
                onOpen={() => openDetail(it.campaign.id, 'applications')}
              />
            ))
          )}
        </div>
      )}

      {tab === 'received-submissions' && (
        <div className="space-y-2">
          <p className="text-xs ui-muted">Bài creator nộp cho brief sáng tạo hoặc đơn nhận đề xuất của bạn, đang chờ bạn duyệt.</p>
          <LoadErrorNote failed={failedCampaigns} onRetry={retryRequests} />
          {requestsLoading ? (
            <p className="text-sm ui-muted">Đang tải…</p>
          ) : receivedSubmissions.length === 0 ? (
            <p className="text-sm ui-muted">Chưa có bài nộp nào chờ duyệt.</p>
          ) : (
            receivedSubmissions.map((it) => (
              <ReceivedSubmissionRow
                key={it.task.dealId}
                item={it}
                nowMs={nowMs}
                onOpen={() => setView({ mode: 'task-review', dealId: it.task.dealId })}
              />
            ))
          )}
        </div>
      )}

      {tab === 'ratings' && (
        <div className="flex flex-col gap-3">
          <OrdererProfileLinks accounts={bizAccounts} />
          <BrandRatingPendingList ref={ratingsRef} viewerRole="Business" onCountChange={setPendingRatingsCount} />
        </div>
      )}

      {tab === 'sanctions' && (
        <div className="space-y-2">
          <p className="text-xs ui-muted">Các lần tài khoản đặt đơn của bạn bị xử phạt trên sàn hợp tác.</p>
          <OrdererSanctionHistory />
        </div>
      )}
    </div>
  );
};

// Provider bọc ngoài để cả màn tạo/sửa, chi tiết chiến dịch và bàn duyệt bài cùng đọc một trạng thái khoá (RB7).
// Cờ khoá sàn cũ của tài khoản vẫn tính là đang khoá khi chưa tải được lần phạt mới.
const BrandCampaignSection: React.FC<BrandCampaignSectionProps> = ({ bizAccounts }) => {
  const banned = bizAccounts.find((a) => a.marketplaceBanned);
  return (
    <OrdererSanctionProvider legacyBanned={!!banned} legacyBanReason={banned?.marketplaceBanReason ?? null}>
      <BrandCampaignSectionInner bizAccounts={bizAccounts} />
    </OrdererSanctionProvider>
  );
};

export default BrandCampaignSection;
